import React from "react";
import { Container, Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useBrand } from "../brand/BrandContext";
import { resolveBrandContacts } from "../brand/contacts";
import { COMPANY_CONTACTS } from "../constants/contacts";
import TenantLegalNotice from "../components/TenantLegalNotice";
import LegalPageBody from "../components/LegalPageBody";

/** Section keys rendered in order. Values are paragraph key suffixes under publicOffer. */
const SECTIONS = [
  {
    title: "s1Title",
    paragraphs: ["s1P1", "s1P2", "s1P3", "s1P4", "s1P5", "s1P6", "s1P7", "s1P8", "s1P9"],
  },
  {
    title: "s2Title",
    paragraphs: ["s2P1", "s2P2", "s2P3", "s2P4", "s2P5", "s2P6", "s2P7"],
  },
  {
    title: "s3Title",
    paragraphs: ["s3P1", "s3P2", "s3P3", "s3P4", "s3P5", "s3P6", "s3P7"],
  },
  {
    title: "s4Title",
    paragraphs: [
      "s4P1",
      "s4P2",
      "s4P3",
      "s4P4",
      "s4P5",
      "s4P6",
      "s4P7",
      "s4P8",
      "s4P9",
      "s4P10",
      "s4P11",
      "s4P12",
      "s4P13",
      "s4P14",
      "s4P15",
      "s4P16",
    ],
  },
  {
    title: "s5Title",
    paragraphs: ["s5P1", "s5P2", "s5P3", "s5P4", "s5P5", "s5P6"],
  },
  {
    title: "s6Title",
    paragraphs: [
      "s6P1",
      "s6P2",
      "s6P3",
      "s6P4",
      "s6P5",
      "s6P6",
      { subtitle: "s6P7Title", body: "s6P7" },
      { subtitle: "s6P8Title", body: "s6P8" },
      "s6P9",
      "s6P10",
      "s6P11",
    ],
  },
  {
    title: "s7Title",
    paragraphs: ["s7P1", "s7P2", "s7P3", "s7P4", "s7P5", "s7P6", "s7P7", "s7P8"],
  },
  {
    title: "s8Title",
    paragraphs: ["s8P1", "s8P2", "s8P3", "s8P4"],
  },
  {
    title: "s9Title",
    paragraphs: ["s9P1", "s9P2", "s9P3", "s9P4", "s9P5", "s9P6"],
  },
  {
    title: "s10Title",
    paragraphs: ["s10P1", "s10P2", "s10P3"],
  },
  {
    title: "s11Title",
    paragraphs: ["s11P1", "s11P2", "s11P3", "s11P4"],
  },
  {
    title: "s12Title",
    paragraphs: ["s12P1"],
  },
];

const PublicOffer = () => {
  const { t } = useTranslation("common");
  const brand = useBrand();
  const contacts = resolveBrandContacts(brand);
  const offerEmail =
    contacts.email || brand.supportEmail || COMPANY_CONTACTS.email;

  const linkifyEmail = (text) => {
    const parts = text.split(offerEmail);
    if (parts.length !== 2) return text;
    return (
      <>
        {parts[0]}
        <a href={`mailto:${offerEmail}`}>{offerEmail}</a>
        {parts[1]}
      </>
    );
  };

  const defaultBody = (
    <>
      <Typography variant="body2" color="textSecondary" gutterBottom textAlign="center">
        {t("publicOffer.lastUpdated")}
      </Typography>

      {SECTIONS.map((section) => (
        <Box key={section.title} mt={6}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
            {t(`publicOffer.${section.title}`)}
          </Typography>
          {section.paragraphs.map((item, idx) => {
            if (typeof item === "object" && item.subtitle) {
              return (
                <React.Fragment key={`${section.title}-${idx}`}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 1 }}>
                    {t(`publicOffer.${item.subtitle}`)}
                  </Typography>
                  <Typography paragraph sx={{ whiteSpace: "pre-line" }}>
                    {linkifyEmail(t(`publicOffer.${item.body}`))}
                  </Typography>
                </React.Fragment>
              );
            }
            const text = t(`publicOffer.${item}`);
            const isBoldLead =
              item === "s4P1" ||
              item === "s4P6" ||
              item === "s4P10" ||
              item === "s6P1";
            return (
              <Typography
                key={item}
                paragraph
                sx={{ whiteSpace: "pre-line" }}
                component="p"
              >
                {isBoldLead ? <strong>{linkifyEmail(text)}</strong> : linkifyEmail(text)}
              </Typography>
            );
          })}
        </Box>
      ))}
    </>
  );

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: 2,
        mb: 4,
        py: 6,
        backgroundColor: "#fff",
        px: { xs: 2, sm: 4 },
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        textAlign="center"
        sx={{ fontWeight: "bold", mb: 4 }}
      >
        {t("publicOffer.title")}
      </Typography>

      <TenantLegalNotice />

      <LegalPageBody customBody={brand.legalPages?.offer}>
        {defaultBody}
      </LegalPageBody>
    </Container>
  );
};

export default PublicOffer;
